import clsx from 'clsx';
import React from 'react';
import {Body, Icon, Label} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {CreatorBrandSafetyVettingStatus} from '@/types/creatorBrandSafetyVetting';
import {ProfileProps} from '@/types/creatorProfile';
import Conditional from '@/components/conditional';
import CreatorProfileRating from '@/components/creator-profile/components/creator-profile-rating';
import CREATOR_PROFILE_TEST_IDS from '@/components/creator-profile/testIds';
import SafeCollabChip from '@/components/safe-collab-chip';
import styles from './CreatorStatsRow.module.scss';

const TRANSLATION_PREFIX = 'components.creator-profile.header';

// Only terminal + actionable statuses are displayed as badges.
// Other statuses (invited, in_progress, report_failed, etc.) are intentionally hidden.
const DISPLAYABLE_VETTING_STATUSES: Set<CreatorBrandSafetyVettingStatus> =
  new Set(['approved', 'declined', 'ready_for_review']);

interface CreatorStatsRowProps {
  profile: ProfileProps;
  showRating: boolean;
  showGigCount: boolean;
  showAuthenticity?: boolean;
  safeCollabStatus: CreatorBrandSafetyVettingStatus | null;
}

function CreatorStatsRow({
  profile,
  showRating,
  showGigCount,
  showAuthenticity = false,
  safeCollabStatus,
}: CreatorStatsRowProps) {
  const shouldShowSafeCollab =
    safeCollabStatus && DISPLAYABLE_VETTING_STATUSES.has(safeCollabStatus);

  return (
    <div
      className={styles.statsRow}
      data-testid={CREATOR_PROFILE_TEST_IDS.HEADER__STATS_ROW}
    >
      <Conditional condition={showAuthenticity}>
        <div
          className={styles.authenticityBadge}
          data-testid={CREATOR_PROFILE_TEST_IDS.HEADER__AUTHENTICITY_BADGE}
        >
          <Icon
            name="Navigation-Authorized-Filled"
            size="small"
            appearance="neutral"
            aria-hidden="true"
          />
          <Body size="md">{translate('high-authenticity')}</Body>
        </div>
      </Conditional>
      <Conditional condition={showGigCount}>
        <Body
          data-testid={CREATOR_PROFILE_TEST_IDS.HEADER__COMPLETED_GIGS}
          size="md"
        >
          {translate(`${TRANSLATION_PREFIX}.completed-gigs`, {
            amount: profile.completedPaidGigsCount,
          })}
        </Body>
      </Conditional>
      <Conditional condition={showRating}>
        <CreatorProfileRating
          testID={CREATOR_PROFILE_TEST_IDS.HEADER__RATING}
          profile={profile}
        />
      </Conditional>
      <Conditional condition={!!shouldShowSafeCollab}>
        <SafeCollabChip
          status={safeCollabStatus!}
          showLabel
          testID={CREATOR_PROFILE_TEST_IDS.HEADER__SAFE_COLLAB_BADGE}
        />
      </Conditional>
    </div>
  );
}

export default CreatorStatsRow;
